/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { routesForAccessCheck } from '../SideMenuRoutes';
import { toast } from 'react-toastify';

interface MenuDataItem {
	_id: string;
	type: number;
	openInlynk: boolean;
	name: string;
	isEnabled: boolean;
	index: number;
	isOriginalName: boolean;
	linkPath: string;
	icon: string;
	title: string;
	hide?: boolean;
}

interface RouteHelperReturn {
	isRouteAccessible: boolean;
	isLoading: boolean;
	currentRoute: MenuDataItem | null;
	error: string | null;
	menuData: MenuDataItem[];
	currentPath: string;
	matchedSideMenuRoute: any;
}

interface MatchedSideMenuRoute {
	linkPath: string;
	icon: string;
	title: string;
}

const useRouteHelper = (): RouteHelperReturn =>
{
	const menuData = useSelector((state: any) => state?.mysession?.sideMenuData) || [ ];

	const [ isLoading, setIsLoading ] = useState(true);
	const [ error, setError ] = useState<string | null>(null);
	const [ currentRoute, setCurrentRoute ] = useState<MenuDataItem | null>(null);
	const [ isRouteAccessible, setIsRouteAccessible ] = useState(false);
	const [ currentPath, setCurrentPath ] = useState('');
	const [ matchedSideMenuRoute, setMatchedSideMenuRoute ] = useState<any>(null);
	// Function to navigate using pure JavaScript
	const navigateToNews = () =>
	{
		window.history.pushState(null, '', '/news');
		// Trigger a popstate event to notify other components
		window.dispatchEvent(new PopStateEvent('popstate'));
	};

	// Function to get current pathname
	const getCurrentPath = () =>
	{
		return window.location.pathname;
	};

	useEffect(() =>
	{
		const handleLocationChange = () =>
		{
			const path = getCurrentPath();

			setCurrentPath(path);

			if (!menuData || menuData.length === 0)
			{
				setIsLoading(false);

				return;
			}

			// Step 1: Find matching route in SideMenuRoutes
			let matchedSideRoute: string | null = null;

			for (const route of routesForAccessCheck)
			{
				// Check for exact match first
				if (route === path)
				{
					matchedSideRoute = route;
					break;
				}

				// Check for partial match (for nested routes)
				// E.g., '/drive/folder' should match '/drive/folder/123'
				if (path.startsWith(route) && route !== '/')
				{
					matchedSideRoute = route;
					break;
				}
			}

			setMatchedSideMenuRoute(matchedSideRoute);

			// If no route found in SideMenuRoutes, allow access (might be a public route)
			if (!matchedSideRoute)
			{
				setIsRouteAccessible(true);
				setError(null);
				setCurrentRoute(null);
				setIsLoading(false);

				return;
			}

			// Step 2: Find corresponding menu item in menuData by linkPath
			const correspondingMenuItem = menuData.find((item: MenuDataItem) =>
				matchedSideRoute && (item.linkPath === matchedSideRoute || item.linkPath.startsWith(matchedSideRoute))
			);

			setCurrentRoute(correspondingMenuItem || null);

			// Step 3: Check if the route exists in menuData and is enabled
			if (!correspondingMenuItem || !correspondingMenuItem.isEnabled)
			{
				// Route exists in SideMenuRoutes but not in menuData - deny access
				setIsRouteAccessible(false);
				setError('Access denied. You do not have permission to view this page.');
				toast.error('Access denied. You do not have permission to view this page.');
				navigateToNews();
			}
			else
			{
				// Route exists in both SideMenuRoutes and menuData, and is enabled - allow access
				setIsRouteAccessible(true);
				setError(null);
			}

			setIsLoading(false);
		};

		// Initial check
		handleLocationChange();

		// Listen for browser navigation events
		window.addEventListener('popstate', handleLocationChange);

		// Listen for custom navigation events (if other parts of app use pushState)
		window.addEventListener('locationchange', handleLocationChange);

		// Cleanup
		return () =>
		{
			window.removeEventListener('popstate', handleLocationChange);
			window.removeEventListener('locationchange', handleLocationChange);
		};
	}, [ menuData ]);

	return {
		isRouteAccessible,
		isLoading,
		currentRoute,
		error,
		menuData,
		currentPath,
		matchedSideMenuRoute
	};
};

export default useRouteHelper;
